import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { AlertCircle, UserX, Activity, Target, ArrowUpRight } from "lucide-react";

const painPoints = [
  {
    icon: Activity,
    accent: "#3BA7D1",
    tint: "rgba(59,167,209,0.12)",
    title: "Buyers stuck in mid-funnel.",
    desc: "Not because they&apos;ve lost interest — because they need help making a confident decision. We hand-hold with content that answers, builds trust, and empowers next-step action.",
    tag: "Mid-funnel stall",
    slug: "b2b-content-marketing",
    image:
      "https://images.unsplash.com/photo-1524178232363-1fb2b075b655?q=80&w=1400&auto=format&fit=crop",
    imageAlt: "Marketer stuck reviewing mid-funnel data",
  },
  {
    icon: UserX,
    accent: "#E96A7F",
    tint: "rgba(233,106,127,0.12)",
    title: "Got qualified leads. Then silence.",
    desc: "It&apos;s not rejection — it&apos;s hesitation. We nurture with strategic programs that answer last-mile questions and reinforce your value, turning intent into action.",
    tag: "Lead silence",
    slug: "b2b-nurture-marketing",
    image:
      "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?q=80&w=1400&auto=format&fit=crop",
    imageAlt: "Empty meeting room — no response",
  },
  {
    icon: AlertCircle,
    accent: "#FFB000",
    tint: "rgba(255,176,0,0.14)",
    title: "Inconsistent leads. Unpredictable pipeline.",
    desc: "Sales teams can&apos;t plan when the pipeline swings. We build a demand engine for consistency — powered by intent data, smart qualification, and always-on demand gen.",
    tag: "Pipeline chaos",
    slug: "b2b-demand-generation",
    image:
      "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?q=80&w=1400&auto=format&fit=crop",
    imageAlt: "Whiteboard chaos of plans and sticky notes",
  },
  {
    icon: Target,
    accent: "#6B77E8",
    tint: "rgba(107,119,232,0.12)",
    title: "Key accounts aren&apos;t engaging.",
    desc: "When marketing casts too wide, sales can&apos;t focus. Our ABM programs zero in on high-value accounts with personalized outreach and real-time engagement visibility.",
    tag: "ABM misalignment",
    slug: "abm-cx-kaboom",
    image:
      "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?q=80&w=1400&auto=format&fit=crop",
    imageAlt: "Two sales leaders in animated account discussion",
  },
];

export default function PainPoints() {
  return (
    <section
      data-testid="pain-points"
      className="section-light py-section relative overflow-hidden"
    >
      <div className="bg-orbs" />
      <div className="container-xl relative">
        <div className="max-w-4xl mb-12 md:mb-20 text-center mx-auto">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#86868B] mb-5">
            Why B2B marketing feels broken
          </div>
          <h2 className="font-display text-[#1D1D1F] text-4xl md:text-6xl lg:text-7xl tracking-tighter leading-[1.02] font-semibold">
            Four pain points{" "}
            <span className="font-serif italic font-light text-[#86868B]">
              we can fix.
            </span>
          </h2>
          <p className="mt-6 text-[#515154] text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
            Real people, real stalls, real remedies. These are the four most
            common B2B growth blockers we hear from marketing &amp; sales
            leaders — and how we unlock each.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {painPoints.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{
                  duration: 0.7,
                  delay: i * 0.08,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                <Link
                  to={`/services/${p.slug}`}
                  data-testid={`pain-point-${i}`}
                  className="gradient-ring card-lift group relative bg-white rounded-[28px] overflow-hidden border border-black/5 flex flex-col h-full"
                >
                {/* Photo hero */}
                <div className="relative aspect-[16/9] overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.imageAlt}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-[1200ms]"
                    loading="lazy"
                  />
                  <div
                    className="absolute inset-0"
                    style={{
                      background: `linear-gradient(180deg, transparent 0%, transparent 40%, ${p.accent}15 100%), linear-gradient(180deg, rgba(0,0,0,0.25) 0%, transparent 50%)`,
                    }}
                  />

                  {/* Top chips */}
                  <div className="absolute inset-x-0 top-0 p-5 md:p-6 flex items-start justify-between">
                    <div
                      className="h-11 w-11 rounded-2xl flex items-center justify-center backdrop-blur-md border transition-transform duration-500 group-hover:scale-110 group-hover:rotate-6"
                      style={{
                        background: "rgba(255,255,255,0.9)",
                        borderColor: "rgba(255,255,255,0.8)",
                        color: p.accent,
                      }}
                    >
                      <Icon size={18} strokeWidth={1.8} />
                    </div>
                    <span
                      className="text-[10px] uppercase tracking-[0.22em] font-medium px-3 py-1.5 rounded-full backdrop-blur-md border"
                      style={{
                        background: `${p.accent}22`,
                        borderColor: `${p.accent}66`,
                        color: "#fff",
                        textShadow: "0 1px 3px rgba(0,0,0,0.35)",
                      }}
                    >
                      <span
                        className="inline-block h-1.5 w-1.5 rounded-full mr-1.5 align-middle"
                        style={{ background: p.accent }}
                      />
                      {p.tag}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="relative p-7 md:p-9 flex flex-col flex-1">
                  <div
                    className="absolute -top-20 -right-20 h-56 w-56 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700 blur-3xl pointer-events-none"
                    style={{ background: p.tint }}
                  />
                  <h3
                    className="relative font-display text-2xl md:text-3xl tracking-tight text-[#1D1D1F] mb-4 leading-tight font-medium"
                    dangerouslySetInnerHTML={{ __html: p.title }}
                  />
                  <p
                    className="relative text-[#515154] text-base leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: p.desc }}
                  />
                  <div className="relative mt-8 pt-6 border-t border-black/5 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm font-medium text-[#1D1D1F] opacity-80 group-hover:opacity-100 transition-opacity">
                      <span>See how we fix it</span>
                      <span
                        className="h-px w-6 transition-all group-hover:w-10"
                        style={{ background: p.accent }}
                      />
                    </div>
                    <div
                      className="h-9 w-9 rounded-full flex items-center justify-center transition-colors"
                      style={{
                        background: `${p.accent}18`,
                        color: p.accent,
                      }}
                    >
                      <ArrowUpRight size={15} strokeWidth={2.2} />
                    </div>
                  </div>
                </div>
              </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
